import { EyeOff, type LucideIcon } from "lucide-react";
import { useState, type InputHTMLAttributes } from "react";

import { cn } from "./cn";
import { IconButton } from "./IconButton";
import { Eye } from "./motionIcons";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "size"> {
  icon?: LucideIcon;
  invalid?: boolean;
  className?: string | undefined;
}

const BASE =
  "h-9 w-full rounded-md border bg-surface px-3 text-sm text-fg placeholder:text-faint " +
  "outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-60";

export function Input({ icon: Icon, invalid, className, ...rest }: InputProps) {
  return (
    <div className="relative">
      {Icon && (
        <Icon
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-faint"
          aria-hidden
        />
      )}
      <input
        {...rest}
        className={cn(
          BASE,
          Icon && "pl-9",
          invalid ? "border-danger" : "border-line focus:border-accent",
          className,
        )}
      />
    </div>
  );
}

/**
 * Поле пароля с переключателем видимости.
 *
 * §4.1 требует доступную кнопку показать/скрыть. Отдельный компонент, а не
 * проп у Input: у него своё состояние и своя кнопка внутри поля.
 */
export function PasswordInput({ invalid, className, ...rest }: Omit<InputProps, "type" | "icon">) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <input
        {...rest}
        type={visible ? "text" : "password"}
        className={cn(
          BASE,
          "pr-10",
          invalid ? "border-danger" : "border-line focus:border-accent",
          className,
        )}
      />
      <IconButton
        icon={visible ? EyeOff : Eye}
        label={visible ? "Скрыть пароль" : "Показать пароль"}
        size="sm"
        onClick={() => setVisible((v) => !v)}
        className="absolute top-1/2 right-1.5 -translate-y-1/2"
      />
    </div>
  );
}
